import React, { useState } from "react";
import Select from "react-select";
import { Button, ButtonGroup } from "aible";
import { useDispatch, useSelector } from "types/store";
import { isEnabled } from "utils/featureFlags";
import { DEFAULT_MIN_EXPLORE_SAMPLE_SIZE } from "utils/insight/insights";
import { getIsTableauMode } from "containers/App/reducer";
import { handleSelectUseCase, initKPIAnalysis, getDefaultDateIndices } from "containers/ChatGPT/reducer";
import { getComparisonOptions } from "containers/Next/NextReducer";
import { copyAndChangeDatasetSuccess, deleteDataset, getDataset, getDefaultSuccess, getIsOutcomeMulticategorical, getSuccessOptions, updateTargetValue, getIsKPIAnalysis, getKPIDateRange, getKPIAnalysisDateColumn, getIsKPIReady } from "containers/Home/reducer";
import { openCreateModels, openExploration, getIsCurrentDate, getIsReferenceDate } from "containers/Next/NextReducer";
import Tooltip from "components/Inputs/Tooltip";
import { LineInput } from "components/Inputs/LineInput";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import styles from "./style.module.css";
import { getDateAnalysis, getIsLoadingChat, getIsLoadingCreateAI, getIsLoadingExplore, getMinimumValueCount, getOutcome } from "containers/Sense/dataset-selectors";
import { updateTileState } from "containers/Sense/reducer";
import { updateAgentAutonomyView } from "containers/View/reducer";
export const calculateDateValues = (dataset, dateAnalysisState) => {
    const { selectedDateField, currentStart, currentEnd, referenceStart, referenceEnd, dateAnalysisTarget } = dateAnalysisState;
    let fields = dataset?.ranked_fields;
    if (!fields && dataset?.fields) {
        fields = Object.keys(dataset.fields).map((fKey) => dataset.fields[fKey]);
    }
    const dateFieldOptions = fields !== undefined
        ? fields
            .filter((f) => f.type === "date")
            .map((f) => ({
            value: f,
            label: f.label
        }))
        : [];
    const dateField = selectedDateField ??
        dateFieldOptions?.find((o) => o.label === dataset?.options?.timestamp_column)?.value ??
        (dateFieldOptions?.length > 0 ? dateFieldOptions[0]?.value : undefined);
    const dateRowsExcluded = dateField === undefined
        ? 0
        : (dateField?.data ?? [])
            .filter((d) => !getIsCurrentDate(undefined, undefined, d.value, dateAnalysisState) &&
            !getIsReferenceDate(undefined, undefined, d.value, dateAnalysisState))
            .reduce((a, b) => a + b.count, 0);
    const dateRowsBefore = dateField === undefined
        ? 0
        : (dateField?.data ?? [])
            .filter((d) => getIsReferenceDate(undefined, undefined, d.value, dateAnalysisState))
            .reduce((a, b) => a + b.count, 0);
    const dateRowsAfter = dateField === undefined
        ? 0
        : (dateField?.data ?? [])
            .filter((d) => getIsCurrentDate(undefined, undefined, d.value, dateAnalysisState))
            .reduce((a, b) => a + b.count, 0);
    const dateFieldMax = dateField === undefined ? 0 : (dateField?.data ?? []).reduce((a, b) => a + b.count, 0);
    return {
        dateFieldOptions,
        dateField,
        currentStart,
        currentEnd,
        referenceStart,
        referenceEnd,
        dateRowsExcluded,
        dateRowsBefore,
        dateFieldMax,
        dateRowsAfter,
        dateAnalysisTarget: dateAnalysisTarget
    };
};
const ActionsPanel = ({ datasetId }) => {
    const dispatch = useDispatch();
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const outcomeState = useSelector((state) => getOutcome(state, datasetId));
    const outcomeSuccess = outcomeState.outcomeSuccess;
    const updateState = (update) => {
        dispatch(updateTileState(datasetId, update));
    };
    const updateOutcomeSuccess = (outcomeSuccess) => {
        updateState({
            outcome: {
                ...outcomeState,
                outcomeSuccess
            }
        });
    };
    const isLoadingExplore = useSelector((state) => getIsLoadingExplore(state, datasetId));
    const updateIsLoadingExplore = (isLoadingExplore) => updateState({ isLoadingExplore });
    const isLoadingCreateAI = useSelector((state) => getIsLoadingCreateAI(state, datasetId));
    const updateIsLoadingCreateAI = (isLoadingCreateAI) => updateState({ isLoadingCreateAI });
    const isLoadingChat = useSelector((state) => getIsLoadingChat(state, datasetId));
    const minimumValueCounts = useSelector((state) => getMinimumValueCount(state, datasetId));
    const updateMinimumValueCounts = (minimumValueCount) => updateState({ minimumValueCount });
    const dateAnalysisState = useSelector((state) => getDateAnalysis(state, datasetId));
    const isKPIAnalysis = useSelector((state) => getIsKPIAnalysis(state, dataset?.id ?? -1));
    const [showMinimumValueCounts, updateShowMinimumValueCounts] = useState(false);
    const [isLoadingKpi, updateIsLoadingKpi] = useState(false);
    const kpiNextId = "DATASET_" + datasetId;
    const comparisonOptions = useSelector((state) => getComparisonOptions(state, kpiNextId));
    const dateColumn = useSelector((state) => getKPIAnalysisDateColumn(state, datasetId));
    const KPIDateRange = useSelector((state) => getKPIDateRange(state, datasetId));
    const updateDateAnalysisState = (dateAnalysis) => {
        if (dataset?.id) {
            dispatch(updateTileState(dataset?.id, { dateAnalysis }));
        }
    };
    const dateFieldOptions = dataset?.ranked_fields !== undefined
        ? dataset.ranked_fields
            .filter((f) => f.type === "date")
            .map((f) => ({
            value: f,
            label: f.label
        }))
        : [];
    const datasetDateField = dataset?.options?.timestamp_column;
    const datasetTimeBucket = dataset?.options?.kpi_analysis?.time_bucket;
    const checkComparisonOptions = () => {
        const retComparisonOptions = {
            ...KPIDateRange,
            ...comparisonOptions
        };
        // Add this comparisonOptions check which is using the kpiNextId as an argument in getComparisonOptions selector so that this only updates once
        // and prevents the component rerendering from search filtering from dispatching all over again
        if (!comparisonOptions) {
            if (!retComparisonOptions.dateField) {
                if (dateColumn) {
                    retComparisonOptions.dateField = dateColumn;
                }
                else if (datasetDateField) {
                    retComparisonOptions.dateField = datasetDateField;
                }
                else {
                    retComparisonOptions.dateField = dateFieldOptions?.[0]?.label;
                }
            }
            let fields = dataset?.ranked_fields;
            if (!fields && dataset?.fields) {
                fields = Object.keys(dataset.fields).map((fKey) => dataset.fields[fKey]);
            }
            // Null check due to this component being rendered before dataset details finishes downloading
            if (!fields) {
                fields = [];
            }
            const dates = fields.find((f) => f.label === retComparisonOptions?.dateField)?.data;
            const filteredDates = (dates ?? [])?.filter((d) => d?.value !== "{Missing}");
            const checkIsMissingDateOption = (datesArr) => {
                const isMissing = datesArr.some((date) => {
                    if (date === undefined) {
                        return true;
                    }
                    const foundDate = filteredDates?.find((fDate) => fDate?.value === date);
                    if (!foundDate) {
                        return true;
                    }
                    return false;
                });
                return isMissing;
            };
            const comparisonDates = [
                retComparisonOptions?.currentEnd,
                retComparisonOptions?.currentStart,
                retComparisonOptions?.referenceEnd,
                retComparisonOptions?.referenceStart
            ];
            const missingDateRange = checkIsMissingDateOption(comparisonDates);
            // Populating the initial comparisonOption date references if they don't exist or if a date is not found in
            // filteredDates (fixes broken state but now requires all valid dates to be saved correctly).
            // Using new function to check default date indices, 3 months start/end, 4 weeks start/end, 7 days (or whatever date unit) start/end otherwise
            if (retComparisonOptions?.dateField && missingDateRange) {
                const { refStart, refEnd, currStart, currEnd } = getDefaultDateIndices(filteredDates, datasetTimeBucket);
                retComparisonOptions.referenceStart = filteredDates[refStart]?.value;
                retComparisonOptions.referenceEnd = filteredDates[refEnd]?.value;
                retComparisonOptions.currentStart = filteredDates[currStart]?.value;
                retComparisonOptions.currentEnd = filteredDates[currEnd]?.value;
            }
        }
        updateDateAnalysisState({ ...retComparisonOptions });
        return retComparisonOptions;
    };
    const handleStartKpiWhatsChanged = () => {
        updateIsLoadingKpi(true);
        const newComparisonOptions = checkComparisonOptions();
        dispatch(openExploration(dataset?.id, outcomeValue, newComparisonOptions, 0))
            .catch((e) => console.log(e))
            .finally(() => updateIsLoadingKpi(false));
    };
    const { dateField, currentStart, currentEnd, referenceStart, referenceEnd, dateRowsExcluded, dateRowsBefore, dateFieldMax, dateRowsAfter, dateAnalysisTarget } = calculateDateValues(dataset, dateAnalysisState);
    const isError = dataset?.dataset_status === "ERROR";
    const isContinuous = dataset?.fields[dataset?.outcome ?? ""]?.type === "number";
    // const outcomeOptions =
    //   dataset?.fields?.[dataset?.outcome]?.type !== "number" &&
    //   dataset?.fields?.[dataset?.outcome]?.data
    //     ? dataset?.fields?.[dataset?.outcome]?.data.map((d) => ({
    //         value: d,
    //         label: d.value,
    //       }))
    //     : undefined;
    const isTableauMode = useSelector((state) => getIsTableauMode(state));
    const successOptions = useSelector((state) => getSuccessOptions(state, datasetId, dataset, dataset?.outcome));
    const defaultSuccess = useSelector((state) => getDefaultSuccess(state, datasetId));
    const isMulticategorical = useSelector((state) => getIsOutcomeMulticategorical(state, datasetId));
    const outcomeField = dataset?.fields?.[dataset?.outcome ?? ""];
    const outcomeOptions = outcomeField?.type !== "number" && outcomeField?.data
        ? successOptions?.map((o) => ({
            value: o.value,
            label: o.label
        }))
        : undefined;
    const outcomeValue = outcomeSuccess ?? outcomeOptions?.find((o) => o?.value === defaultSuccess) ?? outcomeOptions?.[0];
    const showReevaluate = isMulticategorical && outcomeValue?.value !== defaultSuccess;
    const showKpiOverview = useSelector((state) => getIsKPIReady(state, datasetId));
    const datasetFields = Object.values(dataset?.fields ?? {});
    const retDatasetFields = dataset?.ranked_fields ?? datasetFields;
    const datasetDateOptions = retDatasetFields.filter((f) => f.type === "date");
    // Now show kpi deep dive button if there's a dateField option but disable it if there are no valid comparison options to use
    const showKpiWhatsChanged = Boolean(isEnabled("dataset-whats-changed") && datasetDateOptions?.length > 0);
    const disableKpiWhatsChanged = !datasetTimeBucket;
    if (isError) {
        return (React.createElement("div", { className: styles.openOverview, style: {
                maxHeight: "500px",
                width: "100%",
                overflow: "hidden",
                marginLeft: "10px"
            } },
            React.createElement("div", { style: {
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center"
                } },
                React.createElement(Button, { onClick: () => dispatch(deleteDataset(datasetId)) }, "Delete Evaluation"))));
    }
    if (!(isEnabled("aible-optimize") || isEnabled("aible-explore") || isEnabled("chat-aible"))) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement("div", { className: styles.openOverview, style: {
            maxHeight: "500px",
            width: "calc(100% - 20px)",
            overflow: "show",
            marginLeft: "10px"
        } },
        React.createElement("div", { className: styles.senseActionsBody, style: { flexDirection: "row" } }, isContinuous ? (React.createElement(React.Fragment, null,
            React.createElement("span", { style: { whiteSpace: "pre" } }, "How would you like to proceed with "),
            React.createElement("strong", { style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" } }, dataset?.outcome),
            React.createElement("span", { style: { whiteSpace: "pre" } }, "?"))) : (React.createElement(React.Fragment, null,
            React.createElement("span", { style: { whiteSpace: "pre" } }, "What outcome of "),
            React.createElement("strong", { style: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" } }, dataset?.outcome),
            React.createElement("span", { style: { whiteSpace: "pre" } }, " would you like to proceed with?")))),
        React.createElement("div", { className: styles.dButtons, style: outcomeOptions === undefined ? { justifyContent: "center" } : {} }, (outcomeOptions ?? []).length > 0 && (React.createElement("div", { className: styles.oSelect },
            React.createElement(Select, { options: outcomeOptions, value: outcomeValue, onChange: (val) => {
                    updateOutcomeSuccess(val);
                    if (val !== undefined && !isMulticategorical && dataset?.id) {
                        const newTargetVal = val;
                        dispatch(updateTargetValue(dataset?.id, newTargetVal?.value)).catch((e) => {
                            console.log(e);
                        });
                    }
                }, menuPlacement: "bottom" })))),
        React.createElement("div", { className: styles.dButtons, style: outcomeOptions === undefined ? { justifyContent: "center" } : {} },
            React.createElement(ButtonGroup, { style: { marginLeft: "10px" } }, showReevaluate ? (React.createElement(React.Fragment, null,
                React.createElement(Button, { onClick: () => updateOutcomeSuccess(outcomeOptions?.find((o) => o?.value === defaultSuccess) ?? outcomeOptions?.[0]) }, "Cancel"),
                React.createElement(Button, { onClick: () => {
                        if (dataset?.id === undefined || outcomeSuccess === undefined) {
                            return;
                        }
                        const promiseHelper = () => {
                            updateOutcomeSuccess(undefined);
                        };
                        dispatch(copyAndChangeDatasetSuccess(dataset?.id, outcomeSuccess.value))
                            .then(promiseHelper)
                            .catch(promiseHelper);
                    }, disabled: outcomeSuccess === undefined, type: "primary" }, "Create New Data Analysis"))) : (React.createElement(React.Fragment, null,
                isEnabled("chat-aible") && (React.createElement(Button, { onClick: () => {
                        if (isEnabled("agent-autonomy-controls")) {
                            dispatch(updateAgentAutonomyView("RECOMMENDED"));
                        }
                        dispatch(handleSelectUseCase("CHATAIBLE_ANALYTICS", dataset?.id));
                    }, style: {
                        minWidth: "90px",
                        minHeight: "40px",
                        padding: "9px 14px"
                    }, isLoading: isLoadingChat }, "Agentic Analytics Chat")),
                isEnabled("document-set-kpi-analysis") && isKPIAnalysis && showKpiOverview && (React.createElement(Button, { onClick: () => {
                        const newComparisonOptions = checkComparisonOptions();
                        if (dataset?.id) {
                            dispatch(initKPIAnalysis(dataset?.id, true, newComparisonOptions));
                        }
                    }, style: {
                        minWidth: "90px",
                        minHeight: "40px",
                        padding: "9px 14px"
                    }, isLoading: isLoadingChat }, "What's Changed Overview")),
                showKpiWhatsChanged && (React.createElement(Button, { onClick: handleStartKpiWhatsChanged, style: {
                        minWidth: "90px",
                        minHeight: "40px",
                        padding: "9px 14px"
                    }, isLoading: isLoadingKpi && showKpiWhatsChanged, disabled: disableKpiWhatsChanged }, "What's Changed Deep Dive")),
                isEnabled("show-original-whats-changed-button-flow") &&
                    (isEnabled("aible-optimize") || isEnabled("aible-explore")) &&
                    !isLoadingKpi && (React.createElement(Tooltip, { trigger: ["hover"], overlay: React.createElement("div", { className: styles.dateFieldTooltip }, dateRowsBefore === dateFieldMax
                        ? `Please use the slider to select a date range`
                        : !dateRowsAfter
                            ? dateRowsBefore
                                ? `You are ignoring ${dateRowsExcluded} rows. Please select a current time period.`
                                : `Please use the slider to select a current time period`
                            : `Your exploration will reference the ${dateRowsBefore} rows ` +
                                `between ${referenceStart} and ${referenceEnd}` +
                                `to compare to the ${dateRowsAfter} rows` +
                                `between ${currentStart} and ${currentEnd}`) },
                    React.createElement(Button, { onClick: () => {
                            updateIsLoadingExplore(true);
                            setTimeout(() => dispatch(openExploration(dataset?.id, outcomeValue, {
                                dateField: dateField?.label,
                                currentStart: dateAnalysisState?.currentStart,
                                currentEnd: dateAnalysisState?.currentEnd,
                                referenceStart: dateAnalysisState?.referenceStart,
                                referenceEnd: dateAnalysisState?.referenceEnd
                            }, 0))
                                .catch((e) => console.log(e))
                                .finally(() => updateIsLoadingExplore(false)), 5);
                        }, disabled: !dateRowsAfter || !dateRowsBefore, style: {
                            minWidth: "90px",
                            minHeight: "40px",
                            padding: "9px 14px"
                        }, isLoading: isLoadingExplore }, isLoadingExplore ? (React.createElement(LoadingIndicator, { style: {
                            margin: "0px",
                            transform: "scale(0.5)",
                            position: "absolute"
                        } })) : ("Explore What's Changed for a KPI")))),
                (isEnabled("aible-optimize") || isEnabled("aible-explore") || Boolean(isTableauMode)) && (React.createElement(Button, { onClick: () => {
                        updateIsLoadingExplore(true);
                        setTimeout(() => dispatch(openExploration(dataset?.id, outcomeValue, undefined, minimumValueCounts))
                            .catch((e) => console.log(e))
                            .finally(() => updateIsLoadingExplore(false)), 5);
                    }, style: {
                        minWidth: "80px",
                        minHeight: "40px",
                        padding: "9px 14px"
                    } }, isLoadingExplore ? (React.createElement(React.Fragment, null,
                    "\u00A0",
                    React.createElement(LoadingIndicator, { style: {
                            margin: "0px",
                            transform: "scale(0.5)",
                            position: "absolute"
                        } }))) : ("Explore Insights"))),
                isEnabled("aible-optimize") && !isEnabled("aible-explore") && !isTableauMode && (React.createElement(Tooltip, { trigger: dateRowsBefore &&
                        dateRowsAfter &&
                        (dateAnalysisTarget === "CREATE_AI" || dateAnalysisTarget === "BOTH")
                        ? ["hover"]
                        : [], overlay: React.createElement("div", { className: styles.dateFieldTooltip }, `Your AI will be created using the ${dateRowsBefore} rows ` +
                        `between ${referenceStart} and ${referenceEnd}` +
                        `and will use the the ${dateRowsAfter} rows` +
                        `between ${currentStart} and ${currentEnd} to test the models`) },
                    React.createElement(Button, { onClick: () => {
                            updateIsLoadingCreateAI(true);
                            setTimeout(() => {
                                dispatch(openCreateModels(dataset?.id, outcomeValue, dateAnalysisTarget === "CREATE_AI" || dateAnalysisTarget === "BOTH"
                                    ? {
                                        dateField: dateField?.label,
                                        currentStart: dateAnalysisState?.currentStart,
                                        currentEnd: dateAnalysisState?.currentEnd,
                                        referenceStart: dateAnalysisState?.referenceStart,
                                        referenceEnd: dateAnalysisState?.referenceEnd
                                    }
                                    : undefined))
                                    .catch((e) => console.log(e))
                                    .finally(() => updateIsLoadingCreateAI(false));
                            }, 5);
                        }, style: {
                            minWidth: "72px",
                            padding: "9px 14px"
                        } }, isLoadingCreateAI ? (React.createElement(LoadingIndicator, { style: {
                            margin: "0px",
                            transform: "scale(0.5)",
                            position: "absolute"
                        } })) : ("Create AI")))))))),
        React.createElement("div", { className: styles.tileActionsAdvancedLink },
            React.createElement("a", { onClick: () => updateShowMinimumValueCounts(!showMinimumValueCounts) }, showMinimumValueCounts ? "Hide Advanced Options" : "Show Advanced Options")),
        showMinimumValueCounts ? (React.createElement("div", { className: styles.tileActionsAdvancedWrapper },
            React.createElement("div", { className: styles.tileActionsAdvancedItem },
                React.createElement("div", null, "When exploring, filter out values where the count is less than "),
                React.createElement("div", { className: styles.tileActionsAdvancedItemInput },
                    React.createElement(LineInput, { isSenseBox: true, value: minimumValueCounts ?? DEFAULT_MIN_EXPLORE_SAMPLE_SIZE, onChange: (val) => {
                            const x = Number(val);
                            if (!isNaN(x)) {
                                updateMinimumValueCounts(x);
                            }
                        } }))))) : (React.createElement(React.Fragment, null))));
};
export default ActionsPanel;
